import type { Me, SavedView, SearchHit } from '@ft/shared';
import { canSeePath } from '../lib/area';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n, type Key } from '../i18n';
import { api } from '../lib/http';

type Item = { key: string; group: string; label: string; sub?: string; link: string };

const PAGES: { link: string; key: Key; words: string }[] = [
  { link: '/', key: 'nav.start', words: 'start cockpit home' },
  { link: '/bestellungen', key: 'nav.orders', words: 'orders bestellungen po' },
  { link: '/vertraege', key: 'nav.contracts', words: 'verträge vertraege contracts rahmenvertrag budget' },
  { link: '/projekte', key: 'nav.projects', words: 'projects projekte ziele targets' },
  { link: '/invoicing', key: 'nav.invoicing', words: 'invoicing rechnungen invoices' },
  { link: '/forecast', key: 'nav.forecast', words: 'forecast prognose' },
  { link: '/offene-punkte', key: 'nav.issues', words: 'offene punkte issues warnungen' },
  { link: '/hilfe', key: 'nav.help', words: 'hilfe help handbuch' },
];

/**
 * Globale Suche (Strg+K oder /): Bestellungen, Rechnungen, Projekte, Kunden, Seiten und gespeicherte Ansichten.
 * Pfeiltasten wählen, Enter öffnet, Esc schließt.
 */
export function CommandPalette({ me, onClose }: { me: Me; onClose: () => void }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [views, setViews] = useState<SavedView[]>([]);
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => { input.current?.focus(); api<SavedView[]>('GET', '/api/views').then(setViews, () => undefined); }, []);
  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return; }
    const h = setTimeout(() => { api<SearchHit[]>('GET', `/api/search?q=${encodeURIComponent(q.trim())}`).then(setHits, () => setHits([])); }, 150);
    return () => clearTimeout(h);
  }, [q]);

  const items = useMemo<Item[]>(() => {
    const n = q.trim().toLowerCase();
    const match = (s: string) => !n || s.toLowerCase().includes(n);
    const typeLabel = (x: SearchHit['type']) => t(`search.type.${x}`);
    const out: Item[] = hits.map((h) => ({ key: `${h.type}:${h.id}`, group: typeLabel(h.type), label: h.label, sub: h.sub, link: h.link }));
    for (const v of views) if (match(v.name) && canSeePath(me, v.path)) out.push({ key: `view:${v.id}`, group: t('search.views'), label: v.name, sub: v.path, link: v.path });
    for (const p of PAGES) if (canSeePath(me, p.link) && match(`${t(p.key)} ${p.words}`)) out.push({ key: `page:${p.link}`, group: t('search.pages'), label: t(p.key), link: p.link });
    return out;
  }, [hits, views, q, t, me]);
  useEffect(() => setSel(0), [q, hits.length]);

  const go = (it: Item | undefined) => { if (!it) return; onClose(); nav(it.link); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(items.length - 1, s + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(items[sel]); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  let lastGroup = '';
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="cmdk" role="dialog" aria-modal="true" aria-label={t('search.title')}>
        <div className="cmdk-in">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
          <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder={t('search.placeholder')}
            role="combobox" aria-expanded="true" aria-controls="cmdk-list" aria-activedescendant={items[sel] ? `cmdk-${sel}` : undefined} autoComplete="off" />
          <kbd>Esc</kbd>
        </div>
        <ul className="cmdk-list" id="cmdk-list" role="listbox">
          {!items.length && <li className="cmdk-empty">{q.trim().length < 2 ? t('search.hint') : t('search.none')}</li>}
          {items.map((it, i) => {
            const head = it.group !== lastGroup ? (lastGroup = it.group) : null;
            return (
              <li key={it.key} role="presentation">
                {head && <div className="cmdk-group">{head}</div>}
                <div id={`cmdk-${i}`} role="option" aria-selected={i === sel} className={`cmdk-item${i === sel ? ' sel' : ''}`} onMouseEnter={() => setSel(i)} onMouseDown={(e) => { e.preventDefault(); go(it); }}>
                  <span className="l">{it.label}</span>{it.sub && <span className="s">{it.sub}</span>}
                </div>
              </li>
            );
          })}
        </ul>
        <div className="cmdk-foot"><span><kbd>↑</kbd><kbd>↓</kbd> {t('search.move')}</span><span><kbd>Enter</kbd> {t('search.open')}</span><span><kbd>?</kbd> {t('keys.title')}</span></div>
      </div>
    </>
  );
}

export const SHORTCUTS: { keys: string[]; key: Key }[] = [
  { keys: ['Strg', 'K'], key: 'keys.search' },
  { keys: ['/'], key: 'keys.search' },
  { keys: ['N'], key: 'keys.new' },
  { keys: ['G', 'S'], key: 'keys.goStart' },
  { keys: ['G', 'B'], key: 'keys.goOrders' },
  { keys: ['G', 'V'], key: 'keys.goContracts' },
  { keys: ['G', 'P'], key: 'keys.goProjects' },
  { keys: ['G', 'I'], key: 'keys.goInvoicing' },
  { keys: ['G', 'F'], key: 'keys.goForecast' },
  { keys: ['G', 'O'], key: 'keys.goIssues' },
  { keys: ['?'], key: 'keys.help' },
];

export function ShortcutHelp({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === 'Escape' || e.key === '?') onClose(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="cmdk keys" role="dialog" aria-modal="true" aria-label={t('keys.title')}>
        <div className="cmdk-group" style={{ padding: '14px 18px 6px' }}>{t('keys.title')}</div>
        <ul className="keylist">
          {SHORTCUTS.map((s) => <li key={s.keys.join('+') + s.key}><span>{t(s.key)}</span><span>{s.keys.map((k, i) => <kbd key={i}>{k === 'Strg' ? t('keys.ctrl') : k}</kbd>)}</span></li>)}
        </ul>
        <div className="cmdk-foot"><span>{t('keys.thenHint')}</span><button className="btn ghost" onClick={onClose}>{t('common.close')}</button></div>
      </div>
    </>
  );
}

/** Tastenkürzel: g + Buchstabe springt zu einer Seite, n = neu (die Seite entscheidet), / oder Strg+K = Suche, ? = Hilfe. */
export function useShortcuts(open: (what: 'search' | 'keys') => void) {
  const nav = useNavigate();
  useEffect(() => {
    let g = 0;
    const GO: Record<string, string> = { s: '/', b: '/bestellungen', v: '/vertraege', p: '/projekte', i: '/invoicing', f: '/forecast', o: '/offene-punkte', h: '/hilfe' };
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); open('search'); return; }
      const el = e.target as HTMLElement | null;
      if (e.ctrlKey || e.metaKey || e.altKey || (typeof el?.closest === 'function' && el.closest('input,textarea,select,[contenteditable="true"]')) || document.querySelector('[role="dialog"]')) return;
      const k = e.key.toLowerCase();
      if (g && Date.now() - g < 1200 && GO[k]) { e.preventDefault(); g = 0; nav(GO[k]!); return; }
      g = 0;
      if (k === 'g') { g = Date.now(); return; }
      if (e.key === '/') { e.preventDefault(); open('search'); return; }
      if (e.key === '?') { e.preventDefault(); open('keys'); return; }
      if (k === 'n') { window.dispatchEvent(new CustomEvent('ft:new')); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nav, open]);
}

/** Seite reagiert auf „n“ (neu anlegen). */
export function useNewShortcut(fn: () => void, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const h = () => fn();
    window.addEventListener('ft:new', h);
    return () => window.removeEventListener('ft:new', h);
  }, [fn, enabled]);
}
